// SNIGJ - Email Verification Page
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Scale, Sun, Moon, Globe, CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import api from '../lib/api';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const { language, updateLanguage } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setVerifying(false);
      setError(language === 'fr' ? 'Token de vérification manquant' : 'Verification token missing');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await api.get(`/auth/verify-email/${token}`);
      setVerified(true);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'fr' ? 'Erreur de vérification' : 'Verification failed'));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 flex flex-col">
        {/* Top bar */}
        <div className="flex justify-end items-center gap-3 p-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => updateLanguage(language === 'fr' ? 'en' : 'fr')}
          >
            <Globe className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'EN' : 'FR'}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </div>

        {/* Logo */}
        <div className="flex justify-center py-8">
          <div className="w-20 h-20 rounded bg-yellow-400 flex items-center justify-center">
            <Scale className="w-12 h-12 text-slate-900" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex items-start justify-center px-4 sm:px-8">
          <Card className="w-full max-w-md border-0 shadow-xl">
            <CardHeader className="text-center pb-2">
              <CardTitle className="font-heading text-xl sm:text-2xl">
                {language === 'fr' ? 'Vérification Email' : 'Email Verification'}
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                {language === 'fr' 
                  ? 'Activation de votre compte SNIGJ'
                  : 'Activating your SNIGJ account'}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="pt-4">
              {verifying ? (
                <div className="text-center space-y-4 py-8">
                  <Loader2 className="w-16 h-16 mx-auto text-primary animate-spin" />
                  <p className="text-muted-foreground">
                    {language === 'fr' ? 'Vérification en cours...' : 'Verifying...'}
                  </p>
                </div>
              ) : verified ? (
                <div className="text-center space-y-4 py-4">
                  <div className="w-20 h-20 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-xl mb-2 text-green-600">
                      {language === 'fr' ? 'Compte activé !' : 'Account Activated!'}
                    </h3>
                    <p className="text-muted-foreground">
                      {language === 'fr' 
                        ? 'Votre adresse email a été vérifiée avec succès. Vous pouvez maintenant vous connecter à votre compte.'
                        : 'Your email address has been verified successfully. You can now login to your account.'}
                    </p>
                  </div>
                  <div className="pt-4">
                    <Link to="/login">
                      <Button className="w-full bg-green-600 hover:bg-green-700">
                        {language === 'fr' ? 'Se connecter' : 'Login'}
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="text-center space-y-4 py-4">
                  <div className="w-20 h-20 mx-auto rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                    <XCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-xl mb-2 text-red-600">
                      {language === 'fr' ? 'Échec de la vérification' : 'Verification Failed'}
                    </h3>
                    <p className="text-muted-foreground">
                      {error || (language === 'fr' 
                        ? 'Le lien de vérification est invalide ou a expiré.'
                        : 'The verification link is invalid or has expired.')}
                    </p>
                  </div>
                  <div className="pt-4 space-y-3">
                    <Link to="/register">
                      <Button variant="outline" className="w-full">
                        <Mail className="w-4 h-4 mr-2" />
                        {language === 'fr' ? 'Créer un nouveau compte' : 'Create new account'}
                      </Button>
                    </Link>
                    <Link to="/login">
                      <Button className="w-full">
                        {language === 'fr' ? 'Se connecter' : 'Login'}
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="p-4 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} SNIGJ - République Démocratique du Congo
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
